import { Action, createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import { InternalSearchResultType, SearchData, SearchInformationBox } from '@appTypes/GlobalSearchTypes';

// Actions
export enum Actions {
  UPDATE_QUERY = 'internalSiteSearch/UPDATE_QUERY',
  RESET = 'internalSiteSearch/RESET',
  GET_SEARCH_RESULTS_REQUEST = 'internalSiteSearch/GET_SEARCH_RESULTS_REQUEST',
  GET_SEARCH_RESULTS_SUCCESS = 'internalSiteSearch/GET_SEARCH_RESULTS_SUCCESS',
  GET_SEARCH_RESULTS_CANCEL = 'internalSiteSearch/GET_SEARCH_RESULTS_CANCEL',
  GET_SEARCH_RESULTS_ERROR = 'internalSiteSearch/GET_SEARCH_RESULTS_ERROR',
  GET_MORE_SEARCH_RESULTS_REQUEST = 'internalSiteSearch/GET_MORE_SEARCH_RESULTS_REQUEST',
  GET_MORE_SEARCH_RESULTS_SUCCESS = 'internalSiteSearch/GET_MORE_SEARCH_RESULTS_SUCCESS',
  GET_MORE_SEARCH_RESULTS_CANCEL = 'internalSiteSearch/GET_MORE_SEARCH_RESULTS_CANCEL',
  GET_MORE_SEARCH_RESULTS_ERROR = 'internalSiteSearch/GET_MORE_SEARCH_RESULTS_ERROR',
}

export interface State {
  query: string;
  submittedQuery: string;
  isLoading: boolean;
  pages: Record<number, { data: InternalSearchResultType[]; meta: { isLoading: boolean } }>;
  displayCount: number;
  totalResults: number;
  error?: ReduxError;
  currentPage: number;
  searchInformationBox?: SearchInformationBox;
  isFromPopularTerm: boolean;
}

export const initialState: State = {
  query: '',
  submittedQuery: '',
  pages: {},
  isLoading: false,
  displayCount: undefined,
  totalResults: undefined,
  currentPage: undefined,
  searchInformationBox: undefined,
  isFromPopularTerm: false,
};

export const actions = {
  updateQuery: createAction(Actions.UPDATE_QUERY, (query: string) => query)(),
  reset: createAction(Actions.RESET)(),
  getSearchResults: createAsyncAction(
    Actions.GET_SEARCH_RESULTS_REQUEST, // request payload creator
    Actions.GET_SEARCH_RESULTS_SUCCESS, // success payload creator
    Actions.GET_SEARCH_RESULTS_CANCEL, // failure payload creator
    Actions.GET_SEARCH_RESULTS_ERROR, // optional cancel payload creator
  )<boolean | undefined, SearchData, ReduxError, undefined>(),
  getMoreSearchResults: createAsyncAction(
    Actions.GET_MORE_SEARCH_RESULTS_REQUEST, // request payload creator
    Actions.GET_MORE_SEARCH_RESULTS_SUCCESS, // success payload creator
    Actions.GET_MORE_SEARCH_RESULTS_CANCEL, // failure payload creator
    Actions.GET_MORE_SEARCH_RESULTS_ERROR, // optional cancel payload creator
  )<[undefined, undefined], SearchData, ReduxError, undefined>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.updateQuery, (state: State = initialState, action) =>
    produce(state, (draftState) => {
      draftState.query = action.payload;
    }),
  )
  .handleAction(actions.reset, (state: State = initialState) =>
    produce(state, (draftState) => {
      draftState.query = '';
      draftState.submittedQuery = '';
      draftState.pages = {};
      draftState.isLoading = false;
      draftState.displayCount = undefined;
      draftState.totalResults = undefined;
      draftState.currentPage = undefined;
      draftState.searchInformationBox = undefined;
      draftState.isFromPopularTerm = false;
    }),
  )
  .handleAction(actions.getSearchResults.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.pages = { 1: { data: undefined, meta: { isLoading: true } } };
      draftState.displayCount = undefined;
      draftState.totalResults = undefined;
      draftState.currentPage = undefined;
      draftState.isLoading = true;
      draftState.submittedQuery = state.query;
      draftState.isFromPopularTerm = action.payload ?? false;
    }),
  )
  .handleAction(actions.getSearchResults.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.pages = { 1: { data: action.payload?.results, meta: { isLoading: false } } };
      draftState.isLoading = false;
      draftState.displayCount = action.payload?.results?.length;
      draftState.totalResults = action.payload?.meta?.page?.total_results;
      draftState.currentPage = action.payload?.meta?.page?.current;
      draftState.searchInformationBox = action.payload.searchInformationBox;
    }),
  )
  .handleAction(actions.getSearchResults.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      delete draftState.pages[1];
      draftState.isLoading = false;
    }),
  )
  .handleAction(actions.getSearchResults.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      delete draftState.pages[1];
      draftState.isLoading = false;
      draftState.error = action.payload;
    }),
  )
  .handleAction(actions.getMoreSearchResults.request, (state = initialState) =>
    produce(state, (draftState) => {
      draftState.pages[draftState.currentPage + 1] = { data: undefined, meta: { isLoading: true } };
      draftState.isLoading = true;
    }),
  )
  .handleAction(actions.getMoreSearchResults.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.pages[draftState.currentPage + 1] = { data: action.payload?.results, meta: { isLoading: false } };
      draftState.isLoading = false;
      draftState.displayCount += action.payload?.results?.length ?? 0;
      draftState.totalResults = action.payload?.meta?.page?.total_results;
      draftState.currentPage = action.payload?.meta?.page?.current;
    }),
  )
  .handleAction(actions.getMoreSearchResults.cancel, (state = initialState) =>
    produce(state, (draftState) => {
      delete draftState.pages[draftState.currentPage + 1];
      draftState.isLoading = false;
    }),
  )
  .handleAction(actions.getMoreSearchResults.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      delete draftState.pages[draftState.currentPage + 1];
      draftState.isLoading = false;
      draftState.error = action.payload;
    }),
  );
